import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, ArrowLeft, CheckCircle2, XCircle, Mail, ShieldCheck, KeyRound, Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import LanguageSelector from "../components/LanguageSelector";

function VerificationBadge({ verified, t, size = "sm" }) {
  const cls = size === "lg" ? "text-sm px-3 py-1.5" : "text-xs px-2 py-1";
  return verified ? (
    <span
      data-testid="verified-badge"
      className={`inline-flex items-center gap-1 rounded-full bg-green-500/15 text-green-400 border border-green-500/30 font-semibold ${cls}`}
    >
      <CheckCircle2 className="w-4 h-4" /> {`✓ ${t("auth.verified")}`}
    </span>
  ) : (
    <span
      data-testid="not-verified-badge"
      className={`inline-flex items-center gap-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 font-semibold ${cls}`}
    >
      <XCircle className="w-4 h-4" /> {`✗ ${t("auth.notVerified")}`}
    </span>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, sendVerification, verifyEmail, setPassword, verifyAdmin } = useAuth();
  const { t } = useLanguage();

  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pwd, setPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminPwd, setAdminPwd] = useState("");
  const [verifyingAdmin, setVerifyingAdmin] = useState(false);

  if (!user) return null;
  const isGoogle = user.auth_provider === "google";

  const handleVerifyAdmin = async (e) => {
    e.preventDefault();
    if (verifyingAdmin) return;
    setVerifyingAdmin(true);
    try {
      await verifyAdmin(adminPwd);
      setAdminPwd("");
      setAdminOpen(false);
      navigate("/admin");
    } catch (err) {
      toast.error(err.message || t("admin.verifyFailed"));
    } finally {
      setVerifyingAdmin(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (savingPwd) return;
    setSavingPwd(true);
    try {
      await setPassword(pwd);
      toast.success(t("auth.passwordSetToast"));
      setPwd("");
    } catch (err) {
      toast.error(err.message || "Could not save password");
    } finally {
      setSavingPwd(false);
    }
  };

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      await sendVerification();
      toast.success(t("auth.codeSent"));
    } catch (e) {
      toast.error(e.message || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (verifying) return;
    setVerifying(true);
    try {
      await verifyEmail(code.trim());
      toast.success(t("auth.verifiedToast"));
      setCode("");
    } catch (err) {
      toast.error(err.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex flex-col">
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="profile-home-btn"
            className="flex items-center gap-3 text-white hover:opacity-80 transition-opacity"
          >
            <div className="w-10 h-10 bg-neon-cyan/20 rounded flex items-center justify-center">
              <Trophy className="w-6 h-6 text-neon-cyan" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight uppercase">{t("appName")}</h1>
          </button>
          <LanguageSelector />
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-6 py-12">
        <div className="w-full max-w-lg space-y-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1 text-white/40 hover:text-white/70 text-sm transition-colors"
            data-testid="profile-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> {t("back")}
          </button>

          {/* Identity + badge */}
          <div className="card-surface p-6" data-testid="profile-card">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover border border-white/10" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-neon-cyan/20 flex items-center justify-center text-neon-cyan text-2xl font-bold uppercase">
                  {(user.name || user.email || "?").charAt(0)}
                </div>
              )}
              <div className="min-w-0">
                <h2 className="font-heading text-2xl font-bold text-white truncate" data-testid="profile-name">
                  {user.name || user.email}
                </h2>
                <p className="text-white/50 text-sm truncate">{user.email}</p>
                <div className="mt-2">
                  {user.is_owner ? (
                    <button
                      type="button"
                      data-testid="owner-badge"
                      onClick={() => setAdminOpen(true)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-yellow-500/15 text-yellow-400 border border-yellow-500/40 font-bold text-sm px-3 py-1.5 hover:bg-yellow-500/25 transition-colors"
                    >
                      <Crown className="w-4 h-4" /> {t("auth.owner")}
                    </button>
                  ) : (
                    <VerificationBadge verified={!!user.verified} t={t} size="lg" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Email verification section */}
          <div className="card-surface p-6" data-testid="verify-section">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-neon-cyan" />
              <h3 className="font-heading text-lg uppercase text-white">{t("auth.emailVerification")}</h3>
            </div>

            {user.verified ? (
              <p className="text-green-400/90 text-sm flex items-center gap-2" data-testid="verified-note">
                <CheckCircle2 className="w-4 h-4" />
                {isGoogle ? t("auth.googleVerifiedNote") : t("auth.alreadyVerified")}
              </p>
            ) : (
              <>
                <p className="text-white/50 text-sm mb-4">{t("auth.verifyEmailDesc")}</p>

                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="relative">
                    <Mail className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      data-testid="verify-code-input"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder={t("auth.enterCode")}
                      inputMode="numeric"
                      className="input-dark pl-9 tracking-[0.4em] font-data text-lg"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="verify-submit-btn"
                    disabled={verifying || code.length !== 6}
                    className="w-full btn-skewed bg-neon-cyan hover:bg-neon-cyan/90 text-black font-heading font-bold uppercase tracking-widest py-4 disabled:opacity-50"
                  >
                    {verifying ? t("auth.verifying") : t("auth.verifyBtn")}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <Button
                    type="button"
                    data-testid="resend-verification-btn"
                    onClick={handleSend}
                    disabled={sending}
                    variant="ghost"
                    className="text-neon-cyan hover:bg-neon-cyan/10 text-sm"
                  >
                    {sending ? t("auth.sending") : t("auth.resendCode")}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Password sign-in (account linking — lets Google users add a password) */}
          <div className="card-surface p-6" data-testid="password-section">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound className="w-5 h-5 text-neon-cyan" />
              <h3 className="font-heading text-lg uppercase text-white">{t("auth.passwordSection")}</h3>
            </div>
            <p className="text-white/50 text-sm mb-4">
              {user.has_password ? t("auth.changePasswordDesc") : t("auth.createPasswordDesc")}
            </p>
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="relative">
                <KeyRound className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="set-password-input"
                  type="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder={t("auth.newPassword")}
                  className="input-dark pl-9"
                />
              </div>
              <Button
                type="submit"
                data-testid="set-password-btn"
                disabled={savingPwd || pwd.length < 6}
                className="w-full btn-skewed bg-neon-cyan hover:bg-neon-cyan/90 text-black font-heading font-bold uppercase tracking-widest py-4 disabled:opacity-50"
              >
                {savingPwd ? t("auth.savingPassword") : user.has_password ? t("auth.updatePassword") : t("auth.createPassword")}
              </Button>
            </form>
          </div>
        </div>
      </main>

      <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
        <DialogContent className="bg-[#0f0f12] border-white/10 text-white" data-testid="admin-password-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-400">
              <Crown className="w-5 h-5" /> {t("auth.ownerAccess")}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              {t("auth.enterAdminPassword")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVerifyAdmin} className="space-y-4">
            <div className="relative">
              <KeyRound className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="admin-password-input"
                type="password"
                value={adminPwd}
                onChange={(e) => setAdminPwd(e.target.value)}
                placeholder={t("auth.adminPasswordPlaceholder")}
                className="input-dark pl-9"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                data-testid="admin-verify-btn"
                disabled={verifyingAdmin || adminPwd.length < 1}
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-heading font-bold uppercase tracking-widest py-4 disabled:opacity-50"
              >
                {verifyingAdmin ? t("auth.verifyingOwner") : t("auth.verifyOwner")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
